import { FC, lazy, Suspense, useEffect, useMemo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { ErrorBoundary } from 'react-error-boundary';
import { useQuery } from 'react-query';
import { Outlet } from 'react-router-dom';

import Button from '@/components/elements/Button/BaseButton';
import { useLayoutContext } from '@/contexts/layout';
import { useUserContext } from '@/contexts/user';
import useModal from '@/hooks/modal/useModal';
import DmmNav from '@/layouts/DmmNav';
import Footer from '@/layouts/Footer';
import Nav from '@/layouts/Nav';
import {
  ErrorCode,
  RaceProgram,
  useGetRaceProgramsLazyQuery,
} from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { client } from '@/lib/microcms';
import ErrorPage from '@/pages/Error';
import { NewsType } from '@/pages/News/types';

import { ButtonWrapper, Content, Wrapper } from './style';

const CurrentRaceListModal = lazy(
  () => import('@/components/layouts/Modal/CurrentRaceList'),
);

const QuickChargeModal = lazy(
  () => import('@/components/layouts/Modal/Order/QuickCharge'),
);

const LayoutErrorBoundary: FC = ({ children }) => {
  return (
    <ErrorBoundary
      fallbackRender={() => (
        <ErrorPage forceErrorCode={ErrorCode.InternalError} />
      )}
    >
      {children}
    </ErrorBoundary>
  );
};

const Layout: FC = () => {
  const { customStyles, showBetButton } = useLayoutContext();

  const [isOpenQuickChargeModal, setIsOpenQuickChargeModal] = useModal(false);
  const [isOpenCurrentRaceListModal, setIsOpenCurrentRaceListModal] =
    useModal(false);
  const [isOpenDrawer, setIsOpenDrawer] = useModal(false);
  const { isMember, isDisabledMember } = useUserContext();

  const date = useMemo(() => dayjst(), []);

  const nowISO = dayjst().toISOString();
  const dateString = dayjst().format('YYYY-MM-DD');
  const { data: campaigns } = useQuery({
    queryKey: ['current_campaign_top', dateString],
    queryFn: async () =>
      client.getContents(NewsType.Campaign, {
        limit: 100,
        fields: 'id,raceProgramInfo',
        filters: `endTime[greater_than]${nowISO}[and]startTime[less_than]${nowISO}`,
      }),
  });

  const [loadRacePrograms, { data: rpData, loading: isLoadingRP }] =
    useGetRaceProgramsLazyQuery({
      variables: {
        input: {
          heldDate: date,
        },
      },
    });

  useEffect(() => {
    if (isOpenCurrentRaceListModal) {
      loadRacePrograms();
    }
  }, [isOpenCurrentRaceListModal]);

  return (
    <>
      {isMember && (
        <Suspense fallback={<></>}>
          <QuickChargeModal
            isOpen={isOpenQuickChargeModal}
            handleClose={() => setIsOpenQuickChargeModal(false)}
          />
        </Suspense>
      )}
      <Suspense fallback={<></>}>
        <CurrentRaceListModal
          racePrograms={(rpData?.racePrograms || []) as RaceProgram[]}
          campaigns={campaigns}
          isOpen={isOpenCurrentRaceListModal}
          isLoading={isLoadingRP}
          handleClose={() => setIsOpenCurrentRaceListModal(false)}
        />
      </Suspense>
      <Wrapper>
        <DmmNav isMobile={isMobileOnly}>
          <LayoutErrorBoundary>
            {!isDisabledMember && (
              <Nav
                handleOpenQuickChargeModal={() =>
                  setIsOpenQuickChargeModal(true)
                }
                isOpenDrawer={isOpenDrawer}
                setIsOpenDrawer={setIsOpenDrawer}
              />
            )}
            <LayoutErrorBoundary>
              <Content customStyles={customStyles}>
                <Suspense fallback={<></>}>
                  <Outlet />
                </Suspense>
                {showBetButton && (
                  <ButtonWrapper>
                    <Button
                      size="xxl"
                      bgColor="primary"
                      radius="circle"
                      fontWeight="bold"
                      width="112px"
                      height="112px"
                      onClick={() => setIsOpenCurrentRaceListModal(true)}
                    >
                      投票
                    </Button>
                  </ButtonWrapper>
                )}
              </Content>
            </LayoutErrorBoundary>
          </LayoutErrorBoundary>
        </DmmNav>
        <Footer />
      </Wrapper>
    </>
  );
};

export default Layout;
